/**
 * C5Panel — the shared DOM modal surface for C5 "Indigo & Ink" Layer 1.
 *
 * Doctrine: docs/design/ui-c5-indigo-ink.md. Layer 0 (C5Frame) owns the
 * permanent console around the plate; this module owns the surfaces that
 * deliberately cover the plate for a moment — pause, settings, the satchel.
 *
 * Shape of the thing
 * ------------------
 * A panel is a veil (`.c5-p-veil`) mounted inside `#game-root`, so it is
 * clipped to the painting and scales with the frame's `--c5-u` unit. Inside
 * it sits a sheet: either `bare` (paper slips straight on the veil) or
 * `console` (a dark instrument sheet with a glued specimen label).
 *
 * Key handling: while a panel is open it swallows keyboard events so the
 * world scene's movement/hotkey bindings don't fire underneath, but Escape is
 * always allowed through to `window` because the world scenes own the
 * pause/settings Escape flow. Without this, Phaser's captured arrow keys would
 * also `preventDefault()` the range inputs and break slider keyboard access.
 *
 * This module owns no game state. Callers build rows with the exported
 * material helpers and keep their own handles for refresh.
 */

import tokensCss from './c5-tokens.css?inline';
import panelCss from './c5-panel.css?inline';

const STYLE_ELEMENT_ID = 'c5-panel-styles';
const GAME_ROOT_ID = 'game-root';

/** Matches the CSS transition on `.c5-p-veil` (doctrine rule 4: <= 250ms). */
const FADE_MS = 240;

function injectStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = `${tokensCss}\n${panelCss}`;
  document.head.appendChild(style);
}

/** Where panels mount: the plate when the C5 frame is up, else the body. */
export function c5PanelHost(): HTMLElement {
  return document.getElementById(GAME_ROOT_ID) ?? document.body;
}

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type C5PanelVariant = 'bare' | 'console';

export interface C5PanelOptions {
  /** Specimen-label text on the sheet. Pass through getString(). */
  title: string;
  /** `bare` = paper slips on the veil; `console` = dark instrument sheet. */
  variant?: C5PanelVariant;
  /** Sheet width in design units (multiplied by --c5-u). */
  width?: number;
  /** Accessible name; defaults to `title`. */
  ariaLabel?: string;
}

/**
 * A modal DOM surface in the C5 language. Callers append their rows to
 * `body` and drive visibility with show/hide.
 */
export class C5Panel {
  readonly veil: HTMLDivElement;
  readonly sheet: HTMLDivElement;
  readonly body: HTMLDivElement;
  /** Pinned below the body — actions that must survive a scrolling body. */
  readonly footer: HTMLDivElement;

  private readonly labelEl: HTMLDivElement;
  private readonly onKey: (e: KeyboardEvent) => void;
  private open = false;
  private hideTimer: number | null = null;
  private lastFocus: Element | null = null;

  constructor(opts: C5PanelOptions) {
    injectStyles();

    this.veil = document.createElement('div');
    this.veil.className = 'c5-p-veil';
    this.veil.hidden = true;

    this.sheet = document.createElement('div');
    this.sheet.className = `c5-p-sheet c5-p-${opts.variant ?? 'bare'}`;
    this.sheet.setAttribute('role', 'dialog');
    this.sheet.setAttribute('aria-modal', 'true');
    this.sheet.setAttribute('aria-label', opts.ariaLabel ?? opts.title);
    if (opts.width !== undefined) {
      this.sheet.style.width = `calc(${opts.width} * var(--c5-u))`;
    }

    this.labelEl = document.createElement('div');
    this.labelEl.className = 'c5-p-label';
    this.labelEl.textContent = opts.title;

    this.body = document.createElement('div');
    this.body.className = 'c5-p-body';

    this.footer = document.createElement('div');
    this.footer.className = 'c5-p-footer';

    this.sheet.append(this.labelEl, this.body, this.footer);
    this.veil.appendChild(this.sheet);
    c5PanelHost().appendChild(this.veil);

    // Swallow game keys while open; let Escape reach the world scene.
    this.onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return;
      e.stopPropagation();
      if (e.type === 'keydown' && e.key === 'Tab') this.trapTab(e);
    };
    for (const type of ['keydown', 'keyup', 'keypress'] as const) {
      this.veil.addEventListener(type, this.onKey as EventListener);
    }
  }

  setTitle(title: string): void {
    this.labelEl.textContent = title;
  }

  isVisible(): boolean {
    return this.open;
  }

  show(): void {
    if (this.open) return;
    this.open = true;
    if (this.hideTimer !== null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.lastFocus = document.activeElement;
    this.veil.hidden = false;
    // Next frame so the opacity transition has a start value.
    requestAnimationFrame(() => {
      if (this.open) this.veil.classList.add('c5-p-open');
    });
    const first = this.veil.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    this.veil.classList.remove('c5-p-open');
    this.hideTimer = window.setTimeout(() => {
      this.hideTimer = null;
      if (!this.open) this.veil.hidden = true;
    }, FADE_MS);
    if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
    this.lastFocus = null;
  }

  destroy(): void {
    if (this.hideTimer !== null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    for (const type of ['keydown', 'keyup', 'keypress'] as const) {
      this.veil.removeEventListener(type, this.onKey as EventListener);
    }
    this.veil.remove();
  }

  private trapTab(e: KeyboardEvent): void {
    const items = [...this.veil.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

// ── material helpers ───────────────────────────────────────────────────────
// Small builders so every C5 surface speaks the same vocabulary. Each returns
// the element (and, where the value can change, a `refresh` closure).

/** Paper slip button — primary actions. */
export function c5SlipButton(label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'c5-p-slip';
  btn.textContent = label;
  btn.addEventListener('click', onClick);
  return btn;
}

/** Dim band text button — system actions that shouldn't earn paper. */
export function c5QuietButton(label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'c5-p-quiet';
  btn.textContent = label;
  btn.addEventListener('click', onClick);
  return btn;
}

/** Section header: a small specimen tag with a hairline rule. */
export function c5SectionHeader(text: string): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'c5-p-section';
  const tag = document.createElement('span');
  tag.textContent = text;
  const rule = document.createElement('i');
  row.append(tag, rule);
  return row;
}

/** Italic paper note — empty states, hints. */
export function c5Note(text: string): HTMLDivElement {
  const note = document.createElement('div');
  note.className = 'c5-p-note';
  note.textContent = text;
  return note;
}

export interface C5Slider {
  row: HTMLDivElement;
  input: HTMLInputElement;
  /** Re-read the source value and repaint. */
  refresh(): void;
}

/**
 * Ink line with a brass bead. Backed by a native range input so keyboard and
 * pointer drag come for free (see the key-swallow note at the top of the file).
 */
export function c5InkSlider(opts: {
  label: string;
  getValue: () => number;
  setValue: (v: number) => void;
  onInput?: () => void;
}): C5Slider {
  const row = document.createElement('div');
  row.className = 'c5-p-row';

  const label = document.createElement('label');
  label.textContent = opts.label;

  const input = document.createElement('input');
  input.type = 'range';
  input.className = 'c5-p-slider';
  input.min = '0';
  input.max = '100';
  input.step = '1';

  const value = document.createElement('span');
  value.className = 'c5-p-value';

  const paint = (pct: number) => {
    value.textContent = `${pct}%`;
    input.setAttribute('aria-valuetext', `${pct}%`);
  };

  const refresh = () => {
    const pct = Math.round(clamp01(opts.getValue()) * 100);
    input.value = String(pct);
    paint(pct);
  };

  input.addEventListener('input', () => {
    const pct = Number(input.value);
    opts.setValue(clamp01(pct / 100));
    paint(pct);
    opts.onInput?.();
  });

  row.append(label, input, value);
  // Associate the label without needing a unique id in the document.
  label.addEventListener('click', () => input.focus());
  input.setAttribute('aria-label', opts.label);

  refresh();
  return { row, input, refresh };
}

export interface C5Toggle {
  row: HTMLButtonElement;
  refresh(): void;
}

/** Wax seal toggle: sealed = on, faint dashed ring = off. */
export function c5WaxToggle(opts: {
  label: string;
  isOn: () => boolean;
  onToggle: () => void;
}): C5Toggle {
  const row = document.createElement('button');
  row.type = 'button';
  row.className = 'c5-p-row';
  row.setAttribute('role', 'switch');

  const label = document.createElement('span');
  label.className = 'c5-p-rowlabel';
  label.textContent = opts.label;

  const wax = document.createElement('span');
  wax.className = 'c5-p-wax';

  const refresh = () => {
    const on = opts.isOn();
    wax.classList.toggle('c5-p-sealed', on);
    row.setAttribute('aria-checked', on ? 'true' : 'false');
  };

  row.addEventListener('click', () => {
    opts.onToggle();
    refresh();
  });

  row.append(label, wax);
  refresh();
  return { row, refresh };
}

/** Action + key badge row. */
export function c5KeyRow(action: string, key: string): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'c5-p-row';
  const label = document.createElement('span');
  label.className = 'c5-p-rowlabel';
  label.textContent = action;
  const kbd = document.createElement('kbd');
  kbd.className = 'c5-p-kbd';
  kbd.textContent = key;
  row.append(label, kbd);
  return row;
}

/** A grid of dark sockets. `columns` sets the track count. */
export function c5SocketGrid(columns: number): HTMLDivElement {
  const grid = document.createElement('div');
  grid.className = 'c5-p-grid';
  grid.style.gridTemplateColumns = `repeat(${Math.max(1, columns)}, max-content)`;
  return grid;
}

export interface C5SocketOptions {
  /** Glyph or short text on the wax seal. Empty renders an empty socket. */
  glyph?: string;
  /** Accessible name for the socket. */
  ariaLabel: string;
  /** CSS colour for the seal tint (rarity). Defaults to the hanko red. */
  tint?: string;
  /** Small paper count tag. */
  count?: string;
  onClick?: () => void;
  title?: string;
}

/** One socket: wax seal when occupied, faint dashed ring when empty. */
export function c5Socket(opts: C5SocketOptions): HTMLElement {
  const occupied = (opts.glyph ?? '').length > 0;
  const cell = document.createElement(opts.onClick ? 'button' : 'div');
  if (cell instanceof HTMLButtonElement) cell.type = 'button';
  cell.className = occupied ? 'c5-p-cell' : 'c5-p-cell c5-p-empty';
  cell.setAttribute('aria-label', opts.ariaLabel);
  if (opts.title) cell.title = opts.title;
  if (occupied) {
    const seal = document.createElement('span');
    seal.className = 'c5-p-seal';
    seal.textContent = opts.glyph ?? '';
    if (opts.tint) cell.style.setProperty('--c5-p-tint', opts.tint);
    cell.appendChild(seal);
    if (opts.count) {
      const count = document.createElement('span');
      count.className = 'c5-p-count';
      count.textContent = opts.count;
      cell.appendChild(count);
    }
  }
  if (opts.onClick) cell.addEventListener('click', opts.onClick);
  return cell;
}

export interface C5WeightStroke {
  row: HTMLDivElement;
  /** `over` gutters the stroke to the vitality ink; defaults to at-or-past max. */
  set(current: number, max: number, text: string, over?: boolean): void;
}

/** Carry weight as a wet ink stroke that thickens toward the limit. */
export function c5WeightStroke(label: string): C5WeightStroke {
  const row = document.createElement('div');
  row.className = 'c5-p-weight';
  const name = document.createElement('span');
  name.textContent = label;
  const stroke = document.createElement('div');
  stroke.className = 'c5-p-stroke';
  const fill = document.createElement('i');
  stroke.appendChild(fill);
  const value = document.createElement('span');
  row.append(name, stroke, value);

  return {
    row,
    set(current: number, max: number, text: string, over?: boolean) {
      const ratio = max > 0 ? clamp01(current / max) : 0;
      fill.style.width = `${(ratio * 100).toFixed(1)}%`;
      row.classList.toggle('c5-p-over', over ?? (max > 0 && current >= max));
      value.textContent = text;
    },
  };
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.min(1, Math.max(0, v));
}
